import React from 'react';
import PropTypes from 'prop-types';
import Isvg from 'react-inlinesvg';
import { defineMessages, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import Button from 'components/Button';
import Message from 'components/Message';
import { closeModal } from 'components/Modal';
import When from 'components/When';
import { ICONS, ILLUSTRATIONS } from 'constants/icons';
import { bem } from 'helpers/class-name';
import { openDecagonChat } from 'components/Decagon';
import { copyToClipboard } from 'helpers/page-utils';

import './PriceSubmitErrorModal.scss';

const { block, element } = bem('price-submit-error-modal');

const strong = text => <strong>{text}</strong>;

const messages = defineMessages({
  title: {
    id: 'services.price-submit-error-modal.title',
    defaultMessage: 'Uh oh! We were unable to submit pricing for your service',
  },
  description: {
    id: 'services.price-submit-error-modal.description',
    defaultMessage:
      'This can happen due to temporary technical difficulties. Please contact us with the following info to ensure your service pricing is submitted:',
  },
  addPricing: {
    id: 'services.price-submit-error-modal.add-pricing',
    defaultMessage: "Hi, I'd like to add pricing for my service:",
  },
  serviceName: {
    id: 'services.price-submit-error-modal.service-name',
    defaultMessage: 'Service name: <strong>{serviceName}</strong>',
  },
  servicePrice: {
    id: 'services.price-submit-error-modal.service-price',
    defaultMessage: 'Service price: <strong>{servicePrice}</strong>',
  },
  priceLink: {
    id: 'services.price-submit-error-modal.price-link',
    defaultMessage: 'Link to price: <strong>{linkToPrice}</strong>',
  },
  copy: {
    id: 'services.price-submit-error-modal.copy',
    defaultMessage: 'Copy text',
  },
  contactUs: {
    id: 'services.price-submit-error-modal.contact-us',
    defaultMessage: 'Contact us',
  },
  addPricingTemplate: {
    id: 'services.price-submit-error-modal.add-pricing-template',
    defaultMessage: `Hi, I'd like to add pricing for my service:
    Service name: {serviceName}
    Service price: {servicePrice}
    Link to price: {linkToPrice}`,
  },
  addPricingTemplateNotListed: {
    id: 'services.price-submit-error-modal.add-pricing-template-not-listed',
    defaultMessage: `Hi, I'd like to add pricing for my service:
    Service name: {serviceName}
    Service price: {servicePrice}
    Note: This service is not listed online`,
  },
  notListedNote: {
    id: 'services.price-submit-error-modal.not-listed-note',
    defaultMessage: '<strong>Note:</strong> This service is not listed online',
  },
});

export const PRICE_SUBMIT_ERROR_MODAL_NAME = 'PriceSubmitErrorModal';

function PriceSubmitErrorModal({ currencyCode, serviceName, linkToPrice, servicePrice, notListedOnline }) {
  const dispatch = useDispatch();
  const intl = useIntl();
  const formattedServicePrice = intl.formatNumber(servicePrice, { style: 'currency', currency: currencyCode });

  const handleClose = () => {
    dispatch(closeModal());
  };

  const handleCopyToClipboard = () => {
    const template = notListedOnline ? messages.addPricingTemplateNotListed : messages.addPricingTemplate;
    const values = notListedOnline
      ? { serviceName, servicePrice: formattedServicePrice }
      : { serviceName, servicePrice: formattedServicePrice, linkToPrice };

    const copyText = intl.formatMessage(template, values);
    copyToClipboard(copyText);
  };

  return (
    <div className={block()}>
      <header className={element('header')}>
        <Isvg src={ILLUSTRATIONS.errorTools} className={element('illustration')} />
        <Isvg src={ICONS.cross} className={element('close-icon')} onClick={handleClose} />
      </header>
      <section className={element('content')}>
        <div className={element('content-wrapper')}>
          <Message className={element('text-center', 'text--md', 'text--semibold')} message={messages.title} />
          <Message className={element('text-center')} message={messages.description} />
          <Button
            className={element('copy-btn')}
            onClick={handleCopyToClipboard}
            styled="tertiary"
            type={Button.TYPES.BUTTON}>
            <Isvg src={ICONS.copy2} className={element('copy-icon')} />
            <Message message={messages.copy} />
          </Button>
          <div className={element('pricing-template-container')}>
            <Message message={messages.addPricing} />
            <div className={element('pricing-template')}>
              <Message message={messages.serviceName} values={{ serviceName, strong }} />
              <Message message={messages.servicePrice} values={{ servicePrice: formattedServicePrice, strong }} />
              <When predicate={!notListedOnline}>
                <Message message={messages.priceLink} values={{ linkToPrice, strong }} />
              </When>
              <When predicate={notListedOnline}>
                <Message message={messages.notListedNote} values={{ strong }} />
              </When>
            </div>
          </div>
        </div>
        <Message
          as={Button}
          className={element('contact-btn')}
          message={messages.contactUs}
          onClick={openDecagonChat}
          styled="primary"
          type={Button.TYPES.BUTTON}
        />
      </section>
    </div>
  );
}

PriceSubmitErrorModal.propTypes = {
  currencyCode: PropTypes.string,
  serviceName: PropTypes.string,
  linkToPrice: PropTypes.string,
  servicePrice: PropTypes.string,
  notListedOnline: PropTypes.bool,
};

export default PriceSubmitErrorModal;
